import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box, Flex, Text } from '@chakra-ui/react'
import { SublocationWithDevices } from '@leid/api-locations'
import { Order, OrderItem } from '@leid/api-orders'

import { Loader } from 'components/ui/Loader'
import LeidModal from 'components/ui/Modal/LeidModal'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'

import { getItemsForFlow, getOrderItemFlowSteps } from './utils/flow'
import AsssetsManagementModalHeader from './AsssetsManagementModalHeader'
import { FlowDirection, FlowStepDescription, ReturnAssetsStepData, StepType } from './models'
import PickupReturnFlow from './PickupReturnFlow'

type Props = {
  direction: FlowDirection
  isOpen: boolean
  onClose: () => void
  order: Order
  orderItem: OrderItem
  orderItems: OrderItem[]
  refetchOrder: () => void
  devices: SublocationWithDevices[]
  isPublicPage: boolean
}

const AssetsManagementFlowModal = (props: Props): JSX.Element => {
  const { direction, isOpen, onClose, order, orderItem, orderItems, refetchOrder, devices, isPublicPage } = props

  const { t } = useTranslation('common')
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(true)
  const [currentItemIndex, setCurrentItemIndex] = useState(0)
  const [itemsToProcess, setItemsToProcess] = useState<OrderItem[]>([])
  // States for current item steps handling
  const [flowStepIndex, setFlowStepIndex] = useState(0)
  const [flowSteps, setFlowSteps] = useState<FlowStepDescription[]>([])
  const [selectedAssetVisualTag, setSelectedAssetVisualTag] = useState<string>('')

  const processingItem = itemsToProcess?.[currentItemIndex]
  const activeStep = flowSteps?.[flowStepIndex]

  useEffect(() => {
    if (!isOpen) {
      resetToDefaultState()
      return
    }

    prepareFlow()
  }, [isOpen])

  const resetToDefaultState = () => {
    setIsLoading(true)
    setCurrentItemIndex(0)
    setItemsToProcess([])

    setFlowStepIndex(0)
    setFlowSteps([])
  }

  const prepareFlow = async () => {
    setIsLoading(true)

    const additionalItems = await getItemsForFlow(direction, orderItem, orderItems, order, isPublicPage)
    if (additionalItems === undefined) {
      dispatch(displaySnackbar({ message: t('error'), isError: true }))
      setIsLoading(true)
      handleClose()
      return
    }

    if (additionalItems.length === 0) {
      setIsLoading(true)
      // Probably status has changed, so we need to refetch order anyway
      handleClose(true)
      return
    }

    setItemsToProcess(additionalItems)

    const firstOrderItem = additionalItems[0]
    if (firstOrderItem) {
      const orderItemFlowSteps = await getOrderItemFlowSteps(firstOrderItem, devices, direction)
      if (orderItemFlowSteps === undefined) {
        dispatch(displaySnackbar({ message: t('error'), isError: true }))
        setIsLoading(true)
        handleClose()
        return
      }
      setFlowSteps(orderItemFlowSteps)
    }

    setIsLoading(false)
  }

  const switchToNextItem = async () => {
    setIsLoading(true)
    const nextItemIndex = currentItemIndex + 1
    if (nextItemIndex < itemsToProcess.length) {
      setCurrentItemIndex(nextItemIndex)

      const nextOrderItem = itemsToProcess[nextItemIndex]
      const orderItemFlowSteps = await getOrderItemFlowSteps(nextOrderItem, devices, direction)
      if (orderItemFlowSteps === undefined) {
        dispatch(displaySnackbar({ message: t('error'), isError: true }))
        setIsLoading(true)
        handleClose()
        return
      }
      setFlowStepIndex(0)
      setFlowSteps(orderItemFlowSteps)

      setIsLoading(false)
      return
    }

    // We processed all items - now close
    refetchOrder()
    onClose()
    setIsLoading(false)
  }

  const goToNextFlowStep = () => {
    switchFlowStep(flowStepIndex + 1)
  }

  const goToPreviousFlowStep = () => {
    switchFlowStep(flowStepIndex - 1)
  }

  const switchFlowStep = async (newStepIndex: number) => {
    if (newStepIndex <= 0) {
      setFlowStepIndex(0)
    }

    if (newStepIndex >= flowSteps.length) {
      // We processed all - now finish
      await switchToNextItem()
      return
    }

    setFlowStepIndex(newStepIndex)
  }

  const stopFlow = () => {
    handleClose(true)
  }

  const handleClose = (refetchAnyway = false) => {
    if (currentItemIndex !== 0 || refetchAnyway) {
      refetchOrder()
    }
    onClose()
  }

  const getProductName = () => {
    const productName = processingItem?.productData?.name ?? ''
    return productName
  }

  const getAssetVisualTag = () => {
    if (flowSteps.some((s) => s.type === StepType.RETURN_ASSET)) {
      const step = flowSteps.find((s) => s.type === StepType.RETURN_ASSET)
      const stepData = step?.data as ReturnAssetsStepData
      if (stepData && stepData.asset && stepData.asset.visualTag) {
        return `(${stepData.asset.visualTag})`
      }
    } else if (selectedAssetVisualTag) {
      return `(${selectedAssetVisualTag})`
    }

    return ''
  }

  return (
    <LeidModal
      isOpen={isOpen}
      onClose={() => handleClose()}
      size={{ mdDown: 'full', md: 'xl' }}
      header={
        <AsssetsManagementModalHeader
          flowStepIndex={flowStepIndex}
          flowSteps={flowSteps}
          direction={direction}
          goToPreviousStep={goToPreviousFlowStep}
          onClose={handleClose}
        />
      }
    >
      <Box position="relative" overflow="hidden">
        {itemsToProcess.length > 1 && (
          <Text fontSize="smd" fontWeight={300} textAlign="left" width="100%" marginBottom="24px">
            {t(`pickupProductsCount`, { amount: itemsToProcess.length, current: currentItemIndex + 1 })}
          </Text>
        )}

        <Flex
          key={currentItemIndex}
          className={currentItemIndex !== 0 ? 'page-transition' : ''}
          flexDirection="column"
          justifyContent=" center"
          alignItems="center"
          gridGap="24px"
        >
          <Flex justifyContent="space-between" direction="row" alignItems="center" width="100%">
            <Flex justifyContent="center" alignItems="flex-start" gridGap="4px">
              <Text width="100%" fontWeight={700} fontSize="md" color="text.header">
                <Flex direction="row" gridGap="4px">
                  {getProductName()}{' '}
                  <Box as="span" style={{ whiteSpace: 'nowrap' }}>
                    {getAssetVisualTag()}
                  </Box>
                </Flex>
              </Text>
            </Flex>

            {processingItem?.productData?.products?.[0]?.imageUrl && (
              <Box
                width="100px"
                height="60px"
                background={`url(${processingItem?.productData?.products?.[0]?.imageUrl}) lightgray -5.316px -15.929px / 103.365% 113.274% no-repeat;`}
                backgroundPosition="50% 50%"
                backgroundColor="white"
                backgroundSize="auto 100%"
              />
            )}
          </Flex>

          {isLoading ? (
            <Loader />
          ) : (
            <>
              {processingItem && (
                <PickupReturnFlow
                  stepIndex={flowStepIndex}
                  activeStep={activeStep}
                  goToNextStep={goToNextFlowStep}
                  stopFlow={stopFlow}
                  setSelectedAssetVisualTag={setSelectedAssetVisualTag}
                />
              )}
            </>
          )}
        </Flex>
      </Box>
    </LeidModal>
  )
}

export default AssetsManagementFlowModal
